import React, { useRef, useCallback, useEffect } from 'react';
import { Task } from '../../../../../types/task';

interface FocusProgressBarProps {
  task: Task;
  isPlaying: boolean;
  isDraggingProgressBar: boolean;
  setIsDraggingProgressBar: (dragging: boolean) => void;
  localDragProgress: number | null;
  setLocalDragProgress: (progress: number | null) => void;
  justFinishedDragRef: React.MutableRefObject<boolean>;
  currentProgress: number;
  isTaskProgressDraggable: boolean;
  isFocused: boolean;
  updateUserProgress: (data: { taskId: string; progress: number }) => void;
  trackEvent: (event: string, data: any) => void;
}

/**
 * 专注模式进度条组件
 * 处理进度显示、拖拽交互和进度更新
 */
export const FocusProgressBar: React.FC<FocusProgressBarProps> = ({
  task,
  isPlaying,
  isDraggingProgressBar,
  setIsDraggingProgressBar,
  localDragProgress,
  setLocalDragProgress,
  justFinishedDragRef,
  currentProgress,
  isTaskProgressDraggable,
  isFocused,
  updateUserProgress,
  trackEvent
}) => {
  const progressBarRef = useRef<HTMLDivElement>(null);

  // 从鼠标事件更新进度
  const updateProgressFromMouseEvent = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!progressBarRef.current) return 0;

    // 获取进度条容器的位置和宽度
    const rect = progressBarRef.current.getBoundingClientRect();

    // 计算鼠标位置相对于进度条的百分比
    let newProgress = ((e.clientX - rect.left) / rect.width) * 100;

    // 限制进度在0-100之间
    newProgress = Math.max(0, Math.min(100, newProgress));

    // 更新当前进度
    setLocalDragProgress(newProgress);
    return newProgress;
  }, [setLocalDragProgress]);

  // 处理鼠标移动事件
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingProgressBar) return;
    updateProgressFromMouseEvent(e);
  }, [isDraggingProgressBar, updateProgressFromMouseEvent]);

  // 处理鼠标松开事件
  const handleMouseUp = useCallback((event: MouseEvent) => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = '';
    
    if (progressBarRef.current) {
      progressBarRef.current.style.userSelect = '';
      const draggableChild = progressBarRef.current.firstChild as HTMLElement;
      if (draggableChild) {
        draggableChild.style.userSelect = '';
      }
    }

    // 使用最终进度
    const finalProgress = localDragProgress !== null ? localDragProgress : updateProgressFromMouseEvent(event);

    // 如果是焦点任务，更新全局进度
    if (isFocused && finalProgress !== null) {
      updateUserProgress({ taskId: task.id, progress: finalProgress });
      trackEvent('focus task progress adjusted', {
        task_id: task.id,
        task_title: task.title.substring(0, 50),
        progress: finalProgress,
      });
    }

    // 重置拖动状态
    setIsDraggingProgressBar(false);
    setLocalDragProgress(null);
    justFinishedDragRef.current = true;
  }, [
    handleMouseMove, 
    localDragProgress, 
    updateProgressFromMouseEvent, 
    isFocused, 
    updateUserProgress, 
    task, 
    trackEvent, 
    setIsDraggingProgressBar, 
    setLocalDragProgress, 
    justFinishedDragRef
  ]);

  // 处理进度条鼠标按下事件
  const handleProgressMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isPlaying || !isTaskProgressDraggable) return;

    e.preventDefault();
    e.stopPropagation();

    document.body.style.userSelect = 'none';
    if (progressBarRef.current) {
      progressBarRef.current.style.userSelect = 'none';
      const draggableChild = progressBarRef.current.firstChild as HTMLElement;
      if (draggableChild) {
        draggableChild.style.userSelect = 'none';
      }
    }

    setIsDraggingProgressBar(true);
    justFinishedDragRef.current = false;
    updateProgressFromMouseEvent(e);

    // 添加事件监听
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [
    isPlaying, 
    isTaskProgressDraggable, 
    setIsDraggingProgressBar, 
    justFinishedDragRef, 
    updateProgressFromMouseEvent, 
    handleMouseMove, 
    handleMouseUp
  ]);

  // 计算进度条宽度
  const getProgressWidth = useCallback(() => {
    return `${currentProgress}%`;
  }, [currentProgress]);

  if (!isPlaying) return null;

  return (
    <div
      ref={progressBarRef}
      className={`absolute inset-0 z-0 overflow-hidden ${isTaskProgressDraggable ? 'cursor-pointer' : ''} ${isDraggingProgressBar ? 'select-none' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 进度条 */}
      <div
        className={`h-full bg-ios-blue/10 dark:bg-ios-dark-blue/20 relative ${isTaskProgressDraggable ? 'cursor-ew-resize' : 'cursor-not-allowed'} ${isDraggingProgressBar ? 'select-none' : ''} transition-width duration-500 ease-linear`}
        style={{ width: getProgressWidth() }}
        onMouseDown={isTaskProgressDraggable ? handleProgressMouseDown : undefined}
      >
        {/* 拖动手柄 - 只在可拖动时显示 */}
        {isTaskProgressDraggable && (
          <div
            className={`absolute right-0 top-0 bottom-0 w-4 cursor-ew-resize flex items-center justify-center ${isDraggingProgressBar ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
          >
            <div className="w-1 h-8 bg-ios-blue dark:bg-ios-dark-blue rounded-full opacity-70"></div>
          </div>
        )}
      </div>
    </div>
  );
};
